<template>
  <button type="button" class="btn tooltip-left" @click="emit('buttonClicked')" data-bs-toggle="tooltip" data-bs-placement="right" :data-bs-title="props.description">
    <div class="card justify-items-center" :class="{ active: props.active }" style="width: 140px; height: 160px">
      <div v-if="showDeleteIcon" class="delete-icon__wrapper">
        <span id="deleteModel" @click.stop="emit('deleteProduct')">
          <font-awesome-icon icon="trash" class="text-danger" />
        </span>
      </div>
      <div class="card-body text-center">
        <div class="d-flex justify-content-center">
          <slot></slot>
        </div>
        <div>
          <p class="card-title text-center" ref="titleRef">
            <span :class="{ animated: isOverflowing }">{{ productTitle }}</span>
          </p>
          <p v-if="productSubtitle" class="card-subtitle text-center">{{ productSubtitle }}</p>
        </div>
      </div>
    </div>
  </button>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, getCurrentInstance, ref, nextTick } from 'vue';

import { removeWordFromString } from '@/utils/formBuilder';
import 'bootstrap/dist/css/bootstrap.min.css';
import * as bootstrap from 'bootstrap';

interface Props {
  title: string;
  subtitle?: string;
  description?: string;
  primaryButtonText?: string;
  secondaryButtonText?: string;
  showDeleteIcon?: boolean;
  active?: boolean;
}

const emit = defineEmits(['deleteProduct', 'buttonClicked']);
const props = defineProps<Props>();

const showDeleteIcon = computed(() => {
  return Boolean(props.showDeleteIcon);
});

const productTitle = computed(() => {
  return removeWordFromString(props.title, 'Phase1');
});

const productSubtitle = props.subtitle;

const titleRef = ref<HTMLElement | null>(null);
const isOverflowing = ref(false);

// Scoped references so we only touch tooltips in this component
let tooltipTriggers: HTMLElement[] = [];
let tooltipList: bootstrap.Tooltip[] = [];
let tooltipHandlers: Array<(e: Event) => void> = [];

const updateOverflow = () => {
  if (!titleRef.value) return
  const el = titleRef.value
  const scrollHeight = el.scrollHeight
  const clientHeight = el.clientHeight

  const overflow = scrollHeight - clientHeight

  // 2px tolerance for rounding errors
  isOverflowing.value = overflow > 2

  // Apply the overflow distance as a CSS variable
  el.style.setProperty('--scroll-distance', `-${overflow}px`)
}

onMounted(async () => {
  // Scope query to this component's root to avoid grabbing other cards
  const rootEl = getCurrentInstance()?.proxy?.$el as HTMLElement | undefined;
  tooltipTriggers = rootEl
    ? (Array.from(rootEl.querySelectorAll('[data-bs-toggle="tooltip"]')) as HTMLElement[])
    : [];

  tooltipList = tooltipTriggers.map((el) => bootstrap.Tooltip.getOrCreateInstance(el));

  tooltipTriggers.forEach((el, index) => {
    const handler = () => {
      tooltipList.forEach((t, i) => {
        if (i !== index) t?.hide?.();
      });
      tooltipList[index]?.hide?.();
    };
    tooltipHandlers[index] = handler;
    el.addEventListener('click', handler);
  });

  await nextTick();
  updateOverflow();
});

onUnmounted(() => {
  // Remove listeners first
  tooltipTriggers.forEach((el, index) => {
    const handler = tooltipHandlers[index];
    if (handler) {
      try { el.removeEventListener('click', handler); } catch {}
    }
  });

  tooltipTriggers.forEach((el) => {
    try {
      const inst = bootstrap.Tooltip.getInstance(el);
      inst?.hide?.();
      inst?.dispose?.();
    } catch {}
  });

  // Clear refs
  tooltipList = [];
  tooltipTriggers = [];
  tooltipHandlers = [];
});
</script>

<style scoped lang="scss">
.delete-icon__wrapper {
  position: absolute;
  right: 10px;
  top: 10px;
  cursor: pointer;
}

.btn {
  padding: 0 !important;
}

.card {
  border-color: #1d1e19 !important;
  border-width: 2px !important;
  transition: all 0.1s ease-in-out;
  height: 9rem !important;
  width: 9rem !important;
}

.card.active {
  border-color: #b20000 !important;
  border-width: 4px !important;
}

.card:hover {
  transform: scale(1.03);
}

.card-title {
  padding-top: 0.1rem;
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  overflow: hidden;
  max-width: 11rem;
  max-height: 1.7rem;
  line-height: 1.7rem;
  white-space: normal;
  text-align: center;
  --scroll-distance: 0px; /* default value */
}

.animated {
  display: inline-block;
  animation: scroll-text 6s ease-in-out infinite alternate;
}

@keyframes scroll-text {
  0%, 10% {
    transform: translateY(0);
  }
  90%, 100% {
    transform: translateY(var(--scroll-distance));
  }
}

.card-subtitle {
  padding: 0.1rem;
  font-size: 0.8rem;
  font-weight: 500;
  overflow: hidden;
  max-width: 11rem;
}

.tooltip-inner {
  text-align: left !important;
  max-width: 200px;
}

.tooltip-inner > p {
   text-align: left !important;
 }
</style>
